import { connect } from 'react-redux';
import { Lens, fromTraversable, Prism } from 'monocle-ts';
import { Traversable } from 'fp-ts/lib/Array';
import { isNil, isEmpty, pick, flow, get } from 'lodash';

import container from 'src/ServiceContainer';
import { AppState, AppThunkDispatch } from 'src/store';
import { update } from 'src/services/lenses/Lenses.actions';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import { updateSearch, updateFlowStatus } from 'src/components/Subheader/Subheader.slice';
import { styleEditLens } from 'src/services/lenses/lenses';
import { makeScopeAndFilterSensitive } from 'src/components/higherOrder/ScopeAndFilterSensitive';
import { makePrintSensitive } from 'src/components/higherOrder/Print/PrintSenstive';
import StyleEdit from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit';
import {
  requestStyleEditConfigs,
  receiveStyleEditConfigs,
  cleanupStyleEdit,
  receiveError,
  StyleEditSlice,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.slice';
import {
  StyleEditOwnProps,
  StyleEditValueProps,
  StyleEditConfig,
} from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.types';
import { TenantConfigViewData } from 'src/dao/tenantConfigClient';
import { getProcessedData } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.selectors';
import { FabType, getfabProps, withFab } from 'src/components/higherOrder/withFab';
import { updateAssortmentPlan } from 'src/pages/AssortmentBuild/StyleEdit/StyleEdit.actions';
import { isDataLoaded } from 'src/services/pivotServiceCache';
import { RollupDefn } from 'src/services/configuration/codecs/viewdefns/general';
import { StyleEditViewDefn } from 'src/services/configuration/codecs/viewdefns/viewdefn';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { ConfDefnComponentType } from 'src/services/configuration/codecs/confdefnComponents';
import { AdornmentType } from 'src/services/configuration/codecs/viewdefns/literals';
import { withRouter } from 'src/components/higherOrder/withRouter';
import { getKeyFromRouterSearch } from 'src/pages/NavigationShell/NavigationShell.utils';
import { bindActionCreators } from '@reduxjs/toolkit';
import { toggleCompanionCollapse } from 'src/state/uipose/UiPose.slice';

function mapStateToProps(state: AppState, ownProps: StyleEditOwnProps): StyleEditValueProps {
  const { title, fabType = FabType.none, routerLocation, defns } = ownProps;
  const fabProps = getfabProps(state, fabType);
  const viewState = styleEditLens.get(state);
  const subheader = state.subheader;
  const { filteredStyles, summary, searchKeys, viewDefn } = getProcessedData(state);
  // check for redirect search parameters, if applicable, to set the selected item's index
  let redirectSelection: BasicPivotItem | undefined;
  if (!isNil(routerLocation) && !isEmpty(routerLocation.search)) {
    const queryParamsStyleId = getKeyFromRouterSearch(routerLocation, 'selectedStyleId');

    redirectSelection = filteredStyles.find((style) => {
      return style.id === queryParamsStyleId;
    });
  }

  // if viewDefns haven't loaded, onShowView hasn't been triggered yet
  // viewDataState & isConfigLoading can both be false, while the viewDefn prop is not ready yet, added condition for this
  const isLoading = viewState.isConfigLoading || !isDataLoaded(viewState.viewDataState) || isEmpty(viewDefn);
  const adornments: AdornmentType[] = get(viewDefn, 'adornments', []);
  return {
    title,
    isPrintMode: state.print.isPrintMode,
    isLoading,
    config: viewDefn,
    adornments,
    companionFilters: pick(subheader, ['search', 'flowStatus']),
    previewFilters: pick(subheader, ['altSearch', 'altFlowStatus', 'sortBy']),
    flowStatusOptions: state.appConfig.tenantConfig.flowStatus,
    summary,
    styles: filteredStyles,
    redirectSelection,
    searchKeys,
    viewDataState: viewState.viewDataState,
    fab: fabProps,
    sortByDefn: defns.subheader.sortBy,
    isCompanionCollapsed: state.uiPose.isCompanionCollapsed,
  };
}

export function dispatchToProps(dispatch: AppThunkDispatch, ownProps: StyleEditOwnProps) {
  const { defns } = ownProps;

  return {
    ...bindActionCreators({ onToggleCompanion: toggleCompanionCollapse }, dispatch),
    async onShowView() {
      dispatch(requestStyleEditConfigs());

      try {
        const configs = await container.tenantConfigClient.getTenantViewDefns<StyleEditConfig & TenantConfigViewData>({
          defnIds: defns.view,
          appName: ASSORTMENT,
          validationSchemas: [StyleEditViewDefn, RollupDefn],
        });

        const [viewDefn, rollup] = configs;
        dispatch(receiveStyleEditConfigs({ viewDefn, rollup }));
      } catch (error) {
        dispatch(
          receiveError({
            type: ComponentErrorType.config,
            message: (error as Error)?.message,
            name: ConfDefnComponentType.styleEdit,
            issues: error,
          }),
        );
      }
    },
    onDestroy() {
      dispatch(cleanupStyleEdit());
    },
    onUpdate(styleId: string, propToUpdate: string, propValue: string) {
      const updated = styleEditLens
        // TODO: we have to be smarter about this with caching coming in.
        // If edit occurs while in cacheData, liveData overwrites, leading
        // to "stale" name as name was changed after request but before response.
        // Potential solve: "delay" dispatch until viewDataState === 'liveDataLoaded'
        .compose(Lens.fromPath<StyleEditSlice>()(['liveData', 'tree']))
        .composeTraversal(fromTraversable(Traversable)())
        .composePrism(Prism.fromPredicate((it) => it.id === styleId))
        .modify((item: BasicPivotItem) => {
          return {
            ...item,
            [propToUpdate]: propValue,
          };
        });

      dispatch(update(updated, 'Style Item Updated'));
    },
    onSearchChange(search: string) {
      dispatch(updateSearch(search));
    },
    onFilterChange(values: number[]) {
      dispatch(updateFlowStatus(values));
    },
    updateAssortmentPlan: () => {
      dispatch(updateAssortmentPlan('PlanQueue'));
    },
    onError(error: any) {
      dispatch(
        receiveError({
          type: ComponentErrorType.config,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.styleEdit,
        }),
      );
    },
  };
}

const sensitiveView = flow(() => StyleEdit, withFab, makePrintSensitive, makeScopeAndFilterSensitive)();

export default withRouter(connect(mapStateToProps, dispatchToProps)(sensitiveView));
